import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Send, X, MessageSquareText, AlertTriangle, Link2, ArrowUpRight, ArrowDownLeft, Clock, ChevronDown, ChevronUp } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { Btn } from "./common";
import { overlayCloseHandlers } from "../lib/overlayClose";

/**
 * SmsComposer (Iter96) - Modal reutilizavel para enviar SMS.
 * Uso: <SmsComposer open onClose contactos accountId nome />
 *
 * Fluxo:
 *  - Extrai numeros moveis PT (9XXXXXXXX ou +3519XXXXXXXX) da lista `contactos`.
 *  - Apresenta dropdown "Nome - Departamento (numero)" para escolha rapida.
 *  - Sempre inclui opcao "Introduzir manualmente".
 *  - Se so houver 1 numero, pre-selecciona-o.
 *  - Se nao houver nenhum, entra directamente em modo manual com aviso.
 *  - Templates SMS aplicam variaveis da conta/contact seleccionado.
 *  - Historico rapido: mostra as ultimas 3 mensagens SMS trocadas com o numero.
 *  - Anexar documento: gera shortlink publico para fatura/recibo/proposta/contrato.
 */

// ------------ helpers ----------------
const cleanNum = (raw) => (raw || "").toString().replace(/[\s\-().]/g, "");

const isMobilePT = (raw) => {
  const n = cleanNum(raw);
  if (!n) return false;
  if (/^9\d{8}$/.test(n)) return true;
  if (/^\+3519\d{8}$/.test(n)) return true;
  if (/^003519\d{8}$/.test(n)) return true;
  return false;
};

const normalizeMobile = (raw) => {
  const n = cleanNum(raw);
  if (/^9\d{8}$/.test(n)) return n;
  if (/^\+3519\d{8}$/.test(n)) return n;
  if (/^003519\d{8}$/.test(n)) return "+" + n.slice(2);
  return n;
};

const buildOptions = (contactos = []) => {
  const opts = [];
  for (const c of contactos) {
    const candidatos = [c.telemovel, c.telefone, ...(c.telefones || [])].filter(Boolean);
    const seen = new Set();
    for (const raw of candidatos) {
      if (!isMobilePT(raw)) continue;
      const numero = normalizeMobile(raw);
      if (seen.has(numero)) continue;
      seen.add(numero);
      const dep = c.departamento || c.cargo || "";
      const label = `${c.nome || "Sem nome"}${dep ? ` - ${dep}` : ""} (${numero})`;
      opts.push({ key: `${c.id || c.nome}-${numero}`, label, numero, contactId: c.id || "", nome: c.nome || "" });
    }
  }
  return opts;
};

const shortTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const hoje = new Date();
    const mesmoDia = d.toDateString() === hoje.toDateString();
    return mesmoDia
      ? d.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })
      : d.toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};

// ------------ component ----------------
export default function SmsComposer({ open, onClose, contactos = [], accountId = "", contactId = "", nome = "", onSent }) {
  const [texto, setTexto] = useState("");
  const [selKey, setSelKey] = useState("");
  const [manualNum, setManualNum] = useState("");
  const [templates, setTemplates] = useState([]);
  const [tid, setTid] = useState("");
  const [sending, setSending] = useState(false);
  // historico
  const [historico, setHistorico] = useState([]);
  const [loadingHist, setLoadingHist] = useState(false);
  // documentos anexaveis
  const [docs, setDocs] = useState([]);
  const [anexando, setAnexando] = useState(false);
  const [ttlHoras, setTtlHoras] = useState(168); // default 7 dias
  // agendamento
  const [agendarEnvio, setAgendarEnvio] = useState(false);
  const [dataAgendada, setDataAgendada] = useState("");
  const [horaAgendada, setHoraAgendada] = useState("09:00");

  const options = useMemo(() => buildOptions(contactos), [contactos]);
  const noMobile = options.length === 0;

  useEffect(() => {
    if (!open) return;
    setTexto("");
    setTid("");
    setManualNum("");
    setHistorico([]);
    setAgendarEnvio(false);
    setDataAgendada("");
    setHoraAgendada("09:00");
    if (options.length === 1) setSelKey(options[0].key);
    else if (options.length === 0) setSelKey("manual");
    else setSelKey("");
    (async () => {
      try { const { data } = await api.get("/sms/templates"); setTemplates(data || []); }
      catch { /* noop */ }
      if (accountId) {
        try { const { data } = await api.get(`/crm/accounts/${accountId}/documents`); setDocs(data || []); }
        catch { setDocs([]); }
      }
    })();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedOpt = options.find((o) => o.key === selKey);
  const isManual = selKey === "manual" || noMobile;
  const numeroFinal = isManual ? normalizeMobile(manualNum) : (selectedOpt?.numero || "");
  const contactIdFinal = isManual ? "" : (selectedOpt?.contactId || "");
  const numeroValido = isMobilePT(numeroFinal);

  // Carrega historico quando o numero muda
  useEffect(() => {
    if (!open || !numeroValido) { setHistorico([]); return; }
    let cancel = false;
    setLoadingHist(true);
    (async () => {
      try {
        const { data } = await api.get(`/sms/threads/${encodeURIComponent(numeroFinal)}/messages`);
        if (cancel) return;
        const msgs = (data?.messages || []).slice(-3);
        setHistorico(msgs);
      } catch { if (!cancel) setHistorico([]); }
      finally { if (!cancel) setLoadingHist(false); }
    })();
    return () => { cancel = true; };
  }, [open, numeroFinal, numeroValido]);

  // Se ha template aplicado e o destinatario mudou, re-renderiza para
  // substituir {{primeiro_nome}} etc. com o novo contacto seleccionado.
  useEffect(() => {
    if (!open || !tid) return;
    (async () => {
      try {
        const { data } = await api.get(`/sms/templates/${tid}/render`, { params: { account_id: accountId, contact_id: contactIdFinal } });
        if (data?.ok) setTexto(data.corpo || "");
      } catch { /* noop */ }
    })();
  }, [contactIdFinal]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyTemplate = async (nextTid) => {
    setTid(nextTid);
    if (!nextTid) { setTexto(""); return; }
    try {
      const { data } = await api.get(`/sms/templates/${nextTid}/render`, { params: { account_id: accountId, contact_id: contactIdFinal } });
      if (data?.ok) setTexto(data.corpo || "");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const anexarDoc = async (idx) => {
    if (idx === "" || anexando) return;
    const doc = docs[Number(idx)];
    if (!doc) return;
    setAnexando(true);
    try {
      const { data } = await api.post("/shortlinks", {
        account_id: accountId,
        doc_tipo: doc.tipo,
        doc_ref: doc.doc_ref,
        titulo: doc.titulo,
        ttl_horas: Number(ttlHoras) || 0,
      });
      const url = data?.url;
      if (!url) throw new Error("Sem URL");
      setTexto((prev) => (prev ? prev.trimEnd() + " " : "") + url);
      const validade = Number(ttlHoras) === 0 ? "permanente" : `valido ${Number(ttlHoras) < 48 ? Number(ttlHoras) + "h" : Math.round(Number(ttlHoras) / 24) + "d"}`;
      toast.success(`Link anexado (${validade}).`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha a gerar link."); }
    finally { setAnexando(false); }
  };

  const send = async () => {
    if (!numeroValido || !texto.trim()) return;
    const agendado_para = agendarEnvio && dataAgendada
      ? new Date(`${dataAgendada}T${horaAgendada || "09:00"}:00`).toISOString()
      : null;
    setSending(true);
    try {
      if (agendado_para) {
        await api.post("/scheduled-messages", {
          tipo: "sms",
          numero: numeroFinal,
          mensagem: texto.trim(),
          agendado_para,
          account_id: accountId,
          contact_id: contactId || "",
        });
        toast.success(`SMS agendado para ${new Date(agendado_para).toLocaleString("pt-PT")}.`);
        onSent && onSent();
        onClose && onClose();
      } else {
        const { data } = await api.post("/sms/threads/reply", {
          numero: numeroFinal,
          mensagem: texto.trim(),
          account_id: accountId,
          contact_id: contactId || "",
        });
        if (data.estado === "enviado") {
          toast.success("SMS enviado.");
          onSent && onSent();
          onClose && onClose();
        } else toast.error(data.erro || "Falha ao enviar.");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  if (!open) return null;

  const isUnicode = /[^\x00-\x7f]/.test(texto);
  const segLen = isUnicode ? 70 : 160;
  const segs = texto.length === 0 ? 0 : Math.ceil(texto.length / segLen);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4" {...overlayCloseHandlers(onClose)} data-testid="sms-composer">
      <div className="bg-white rounded-xl w-full max-w-lg mt-16" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-tint">
          <div className="flex items-center gap-2">
            <MessageSquareText size={16} className="text-blue" />
            <h3 className="text-navy font-semibold text-sm">Enviar SMS{nome ? ` - ${nome}` : ""}</h3>
          </div>
          <button onClick={onClose} data-testid="sms-composer-close" className="text-slate-500 hover:text-navy"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3 max-h-[calc(100vh-140px)] overflow-y-auto">
          {noMobile && (
            <div className="flex items-start gap-2 rounded-md p-2.5 bg-amber-50 border border-amber-200 text-[12px] text-amber-800" data-testid="sms-no-mobile">
              <AlertTriangle size={14} className="mt-0.5" />
              <span>Esta conta nao tem contactos com numero de telemovel PT (9XXXXXXXX). Introduza o numero manualmente abaixo.</span>
            </div>
          )}

          {!noMobile && (
            <div>
              <label className="block text-[11px] font-semibold text-navy mb-1 uppercase">Destinatario</label>
              <select value={selKey} onChange={(e) => setSelKey(e.target.value)}
                data-testid="sms-composer-destinatario"
                className="w-full px-3 py-2 text-sm border border-tint rounded bg-white">
                <option value="">-- escolher contacto --</option>
                {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                <option value="manual">Introduzir numero manualmente...</option>
              </select>
            </div>
          )}

          {isManual && (
            <div>
              <label className="block text-[11px] font-semibold text-navy mb-1 uppercase">Numero destino (movel PT)</label>
              <input value={manualNum} onChange={(e) => setManualNum(e.target.value)} placeholder="9XXXXXXXX ou +3519XXXXXXXX"
                data-testid="sms-composer-numero-manual"
                className="w-full px-3 py-2 text-sm border border-tint rounded font-mono" />
              {manualNum && !numeroValido && (
                <div className="text-[11px] text-red-600 mt-1" data-testid="sms-numero-invalido">Formato invalido. Use 9XXXXXXXX ou +3519XXXXXXXX.</div>
              )}
            </div>
          )}

          {numeroValido && (
            <div data-testid="sms-historico" className="rounded-md border border-tint bg-slate-50/60 px-3 py-2">
              <div className="text-[11px] font-semibold text-navy uppercase mb-1.5">Historico rapido</div>
              {loadingHist ? (
                <div className="text-[11px] text-muted2 italic">A carregar...</div>
              ) : historico.length === 0 ? (
                <div className="text-[11px] text-muted2 italic">Sem mensagens anteriores com este numero.</div>
              ) : (
                <ul className="space-y-1.5">
                  {historico.map((m, i) => (
                    <li key={i} className="flex items-start gap-2 text-[11.5px]" data-testid={`sms-historico-${m.direcao}`}>
                      {m.direcao === "in" ? (
                        <ArrowDownLeft size={12} className="text-emerald-600 mt-0.5 shrink-0" />
                      ) : (
                        <ArrowUpRight size={12} className="text-blue mt-0.5 shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-navy break-words line-clamp-2">{m.texto || "(sem texto)"}</div>
                        <div className="text-[10px] text-muted2 mt-0.5">{shortTime(m.em)}{m.estado ? ` - ${m.estado}` : ""}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {templates.length > 0 && (
            <div>
              <label className="block text-[11px] font-semibold text-navy mb-1 uppercase">Template (opcional)</label>
              <select value={tid} onChange={(e) => applyTemplate(e.target.value)}
                data-testid="sms-composer-template"
                className="w-full px-3 py-2 text-sm border border-tint rounded bg-white">
                <option value="">-- escolher template --</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
              </select>
            </div>
          )}

          {docs.length > 0 && (
            <div>
              <label className="block text-[11px] font-semibold text-navy mb-1 uppercase flex items-center gap-1">
                <Link2 size={11} /> Anexar documento (link curto)
              </label>
              <div className="grid grid-cols-3 gap-2">
                <select value={ttlHoras} onChange={(e) => setTtlHoras(Number(e.target.value))}
                  data-testid="sms-composer-anexo-ttl"
                  className="col-span-1 px-2 py-2 text-[12px] border border-tint rounded bg-white">
                  <option value={24}>Valido 24h</option>
                  <option value={168}>Valido 7 dias</option>
                  <option value={720}>Valido 30 dias</option>
                  <option value={0}>Permanente</option>
                </select>
                <select value="" onChange={(e) => anexarDoc(e.target.value)} disabled={anexando}
                  data-testid="sms-composer-anexo"
                  className="col-span-2 px-3 py-2 text-sm border border-tint rounded bg-white">
                  <option value="">{anexando ? "A gerar link..." : "-- inserir link para... --"}</option>
                  {docs.map((d, i) => (
                    <option key={i} value={i}>{d.tipo.toUpperCase()}: {d.titulo}{d.estado ? ` (${d.estado})` : ""}</option>
                  ))}
                </select>
              </div>
              <div className="text-[10px] text-muted2 mt-1">O link e publico e conta acessos. Escolha a validade antes de anexar.</div>
            </div>
          )}

          <div>
            <label className="block text-[11px] font-semibold text-navy mb-1 uppercase flex items-center justify-between">
              <span>Mensagem</span>
              <span className={`text-[10px] font-normal ${segs > 1 ? "text-amber-600" : "text-slate-500"}`}>
                {texto.length} chars {segs > 0 && ` - ${segs} SMS${isUnicode ? " (unicode)" : ""}`}
              </span>
            </label>
            <textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={5}
              maxLength={480} data-testid="sms-composer-texto"
              placeholder="Escreva a sua mensagem..."
              className="w-full px-3 py-2 text-sm border border-tint rounded focus:outline-none focus:ring-2 focus:ring-blue/20" />
          </div>

          {/* Agendar envio */}
          <div className="border border-tint rounded-lg overflow-hidden">
            <button type="button" onClick={() => setAgendarEnvio(!agendarEnvio)}
              data-testid="sms-agendar-toggle"
              className={`w-full flex items-center justify-between px-3 py-2.5 text-xs font-semibold transition ${agendarEnvio ? "bg-blue/5 text-blue" : "text-navy hover:bg-tint"}`}>
              <span className="flex items-center gap-1.5"><Clock size={13} /> Agendar envio</span>
              {agendarEnvio ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>
            {agendarEnvio && (
              <div className="px-3 pb-3 pt-1 space-y-2 border-t border-tint bg-blue/5">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[10px] font-semibold text-navy mb-1 uppercase">Data</label>
                    <input type="date" value={dataAgendada} onChange={(e) => setDataAgendada(e.target.value)}
                      min={new Date().toISOString().split("T")[0]}
                      data-testid="sms-agendar-data"
                      className="w-full px-2 py-1.5 text-sm border border-tint rounded" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-navy mb-1 uppercase">Hora</label>
                    <input type="time" value={horaAgendada} onChange={(e) => setHoraAgendada(e.target.value)}
                      data-testid="sms-agendar-hora"
                      className="w-full px-2 py-1.5 text-sm border border-tint rounded" />
                  </div>
                </div>
                <p className="text-[10.5px] text-muted2">O SMS sera enviado automaticamente na data/hora escolhida.</p>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-tint bg-slate-50">
          <Btn variant="ghost" onClick={onClose} testid="sms-composer-cancel">Cancelar</Btn>
          <Btn onClick={send} disabled={sending || !numeroValido || !texto.trim() || (agendarEnvio && !dataAgendada)} testid="sms-composer-send">
            <Send size={13} /> {sending ? "A enviar..." : agendarEnvio ? "Agendar SMS" : "Enviar SMS"}
          </Btn>
        </div>
      </div>
    </div>
  );
}
